import { Cartesian3, Matrix3 } from "cesium";
import { PointsUtils } from "./PointsUtils";

// getCovarianceMatrix
const scratchDiff = new Cartesian3();

export class MatrixUtils {
  /**
   * Calculate the covariance matrix of a set of points.
   * @param points - The points used to calculate the covariance matrix.
   * @returns The covariance matrix.
   * References:
   * - https://en.wikipedia.org/wiki/Covariance_matrix
   * - https://github.com/CesiumGS/cesium/blob/b44fddb17cc0ffb249abeac8d21c06a788d0d64c/packages/engine/Source/Core/OrientedBoundingBox.js#L141
   * - https://sandcastle.cesium.com/?src=3D%20Tiles%20NGA%20GPM%20Visualization.html&label=All
   */
  static getCovarianceMatrix(points: Cartesian3[]): Matrix3 {
    const centroid = PointsUtils.getCentroid(points);
    const covarianceMatrixArray = Array(3)
      .fill(0)
      .map(() => Array(3).fill(0));

    for (const point of points) {
      const diff = Cartesian3.pack(
        Cartesian3.subtract(point, centroid, scratchDiff),
        [],
        0,
      );
      for (let i = 0; i < 3; i++) {
        for (let j = 0; j < 3; j++) {
          covarianceMatrixArray[i][j] += diff[i] * diff[j];
        }
      }
    }

    // Normalize based on number of points
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 3; j++) {
        covarianceMatrixArray[i][j] /= points.length;
      }
    }

    // prettier-ignore
    return new Matrix3(
      covarianceMatrixArray[0][0], covarianceMatrixArray[0][1], covarianceMatrixArray[0][2],
      covarianceMatrixArray[1][0], covarianceMatrixArray[1][1], covarianceMatrixArray[1][2],
      covarianceMatrixArray[2][0], covarianceMatrixArray[2][1], covarianceMatrixArray[2][2]
    );
  }
}
